import { FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React, { useRef, useEffect, useState } from "react";
import styled, { StyleSheetManager } from "styled-components";

import {
  useDeleteWebDashboardCustomLogo,
  useDeleteWebDashboardCustomTheme,
  useSetWebDashboardCustomLogo,
  useSetWebDashboardCustomThemeDefault,
  useSaveWebDashboardThemeModePreference,
  useWebDashboardCustomThemes,
} from "../../api/webDashboards";
import { CloseIcon, DeleteIcon, Ellipsis, PencilIcon, ThemePaletteIcon } from "../../components";

import {
  BUILTIN_PALETTES,
  CUSTOM_THEME_ID_PREFIX,
  DEFAULT_THEME_ID,
  isToggleAwareThemeId,
  LEGACY_THEME_TO_PALETTE,
  registerCustomThemeSpec,
  ThemeSpec,
} from "./themes";
import { ToggleSwitch } from "./ToggleSwitch";

interface ThemeSelectorProps {
  /** Current theme ID */
  themeId: string;
  /** Live selector mode (light/dark). Toggles read this prop, never local state. */
  mode: string;
  /** Live selector background (on/off). Toggles read this prop, never local state. */
  background: string;
  /** Unified theme-change callback; the partial patch is merged with current state by the parent. */
  handleThemeChange: (next: { themeId?: string; mode?: string; background?: string }) => void;
  /** Target document for event listeners (e.g., iframe document) */
  targetDocument?: Document;
  /** Control open state externally to allow themes menu to be opened from elsewhere on the dashboards page */
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** Opens the custom-theme request modal (owned by the parent). */
  onRequestTheme: () => void;
  /**
   * Live-preview a theme change locally WITHOUT persisting. Palette/toggle clicks fire this so
   * browsing costs no backend writes; the session persists once (via handleThemeChange) on close.
   * Falls back to handleThemeChange (persist-per-click) when not provided.
   */
  onPreviewTheme?: (next: { themeId?: string; mode?: string; background?: string }) => void;
  /**
   * Fired whenever this session's theme state diverges from what the dropdown opened with — on
   * every palette/toggle click (the change is already live on screen) and again when the selector
   * closes and persists. `revertTo` is the theme state when the dropdown opened; the parent shows a
   * "Theme updated" toast whose Reset action feeds it back through handleThemeChange (the only
   * revert path), and each call restarts that toast's countdown.
   */
  onThemeCommitted?: (revertTo: { themeId: string; mode: string; background: string }) => void;
  /**
   * Counterpart to `onThemeCommitted`: the session browsed back to the state the dropdown opened
   * with, so any "Theme updated" toast still on screen now offers a Reset to the current theme.
   * The parent dismisses it.
   */
  onThemeUncommitted?: () => void;
  /**
   * Fired once a confirmed custom-theme delete has landed. The parent shows a toast whose Undo
   * action restores the theme.
   */
  onThemeDeleted?: (deleted: { themeId: string; name: string }) => void;
  /** Fired when the delete request failed; the theme is still there. */
  onThemeDeleteFailed?: () => void;
  /**
   * Edit a custom theme: the parent opens the editor chat prefilled with a theme chip. `colors`
   * are the theme's brand colors — the chip shows the first two instead of a positional name.
   */
  onEditTheme?: (edit: { themeId: string; colors: string[] }) => void;
}

// ---------------------------------------------------------------------------
// Advanced (enterprise admin) section — styled per Figma "Logo & Default theme"
// ---------------------------------------------------------------------------

const CaretWrap = styled.span`
  display: flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
`;

/* Figma types this section's labels in Google Sans Flex and its body text in Proxima Nova; both
   are loaded in the viewer iframe (Google Fonts + the parent app's Typekit kit). Pinned here so
   the section doesn't inherit the dashboard theme's font (e.g. DM Sans on legacy themes). */
const ADVANCED_FONT = '"Google Sans Flex", Inter, system-ui, -apple-system, sans-serif';
const ADVANCED_BODY_FONT = '"proxima-nova", "Proxima Nova", Inter, system-ui, sans-serif';

/* Light mode uses the exact Figma hexes; the design has no dark spec, so dark mode derives the
   same roles from the card tokens instead. */
const AdvancedSection = styled.div<{ $dark: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  font-family: ${ADVANCED_FONT};
  color: ${({ $dark }) => ($dark ? "var(--color-card-foreground, #ffffff)" : "#111215")};
`;

const AdvancedHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

const LogoRow = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 20px;
`;

const LogoSlot = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;

const LogoSlotLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LogoImg = styled.img`
  max-height: 32px;
  max-width: 100%;
  object-fit: contain;
`;

const LogoOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border-radius: 8px;
  background: rgba(221, 221, 221, 0.9);
`;

const LogoPreviewBox = styled.div<{ $darkArtworkBackground: boolean }>`
  position: relative;
  display: flex;
  height: 60px;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid #dddddd;
  padding: 10px 20px;
  background: ${({ $darkArtworkBackground }) => ($darkArtworkBackground ? "#0a0b0f" : "#ffffff")};

  &:hover ${LogoOverlay}, &:focus-within ${LogoOverlay} {
    display: flex;
  }
`;

const RoundIconButton = styled.button`
  display: flex;
  width: 32px;
  height: 32px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 9999px;
  background: #ffffff;
  color: #000000;
  padding: 0;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const LogoDropButton = styled.button<{ $dark: boolean }>`
  display: flex;
  height: 60px;
  width: 100%;
  cursor: pointer;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed ${({ $dark }) => ($dark ? "var(--color-card-border, #3a3b40)" : "#dddddd")};
  border-radius: 8px;
  background: ${({ $dark }) => ($dark ? "var(--color-card-background, #16171b)" : "#ffffff")};
  padding: 10px 20px;
  font-family: inherit;
`;

const DropText = styled.span<{ $dark: boolean }>`
  white-space: nowrap;
  font-family: ${ADVANCED_BODY_FONT};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ $dark }) => ($dark ? "var(--color-card-muted-foreground, #9ca3af)" : "#727272")};
`;

const ChooseFileText = styled.span`
  font-weight: 700;
  text-decoration: underline;
`;

const DefaultThemeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DefaultThemeLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`;

const InfoTooltip = styled.div`
  pointer-events: none;
  position: absolute;
  bottom: 100%;
  left: 0;
  z-index: 20;
  margin-bottom: 4px;
  display: none;
  width: 296px;
  border-radius: 6px;
  background: #ffffff;
  padding: 6px;
  font-family: ${ADVANCED_BODY_FONT};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #111215;
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.08), 0px 16px 12px rgba(0, 0, 0, 0.08);
`;

const InfoWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  color: var(--color-card-muted-foreground, #6b7280);

  &:hover ${InfoTooltip} {
    display: block;
  }
`;

const PickerWrap = styled.div`
  position: relative;
  width: 198px;
`;

const PickerTrigger = styled.button`
  display: flex;
  width: 100%;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font-family: inherit;
`;

const ColorStrip = styled.div`
  display: flex;
  height: 24px;
  min-width: 0;
  flex: 1;
  gap: 2px;
  overflow: hidden;
  border-radius: 8px;
`;

const ColorCell = styled.div`
  height: 100%;
  flex: 1;
`;

const ChooseThemePill = styled.div<{ $dark: boolean }>`
  display: flex;
  height: 24px;
  width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ $dark }) =>
    $dark
      ? "color-mix(in srgb, var(--color-card-foreground, #ffffff) 10%, transparent)"
      : "#eaeaea"};
  color: ${({ $dark }) => ($dark ? "inherit" : "#111215")};
  font-size: 12px;
  font-weight: 500;
`;

const PICKER_MAX_HEIGHT = 260;
const PICKER_TRIGGER_GAP = 6;

/* Fixed-positioned (coords measured from the trigger) so it can overflow the theme panel's
   scroll clip; opens downward, flipping up only when the viewport lacks room below. */
const PickerPanel = styled.div<{ $dark: boolean }>`
  position: fixed;
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: ${({ $dark }) => ($dark ? "var(--color-card-background, #16171b)" : "#ffffff")};
  color: ${({ $dark }) => ($dark ? "var(--color-card-foreground, #ffffff)" : "#111215")};
  padding: 12px;
  /* 8 built-ins + custom themes can outgrow the viewport slice; scroll inside the panel. */
  max-height: ${PICKER_MAX_HEIGHT}px;
  overflow-y: auto;
  box-shadow: ${({ $dark }) =>
    $dark
      ? "0px 0px 16px rgba(0, 0, 0, 0.4), 0px 16px 24px rgba(0, 0, 0, 0.4)"
      : "0px 0px 16px #ebebeb, 0px 16px 24px #ebebeb"};
`;

const PickerHeader = styled.div`
  padding-bottom: 8px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
`;

const PickerSectionLabel = styled.div`
  padding: 12px 0 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--color-card-muted-foreground, #727272);
`;

/* The ring is an outward box-shadow (into the option wrapper's 2px padding) rather than a
   border, so the pill keeps the same 24px of fill as the color swatches. */
const NoDefaultPill = styled.div<{ $dark: boolean; $selected: boolean }>`
  display: flex;
  height: 24px;
  width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 0 0 0 2px
    ${({ $dark, $selected }) =>
      $selected
        ? $dark
          ? "#ffffff"
          : "var(--color-primary-button-background, #4f46e5)"
        : $dark
        ? "var(--color-card-border, #3a3b40)"
        : "#dddddd"};
  background: ${({ $dark }) =>
    $dark
      ? "color-mix(in srgb, var(--color-card-foreground, #ffffff) 10%, transparent)"
      : "#ffffff"};
  color: ${({ $dark }) => ($dark ? "var(--color-card-muted-foreground, #9ca3af)" : "#727272")};
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
`;

const PickerOption = styled.button<{ $selected: boolean; $darkMode: boolean }>`
  display: flex;
  width: 100%;
  min-width: 0;
  cursor: pointer;
  align-items: center;
  border-radius: 10px;
  border: 2px solid
    ${({ $selected, $darkMode }) =>
      $selected
        ? $darkMode
          ? "#ffffff"
          : "var(--color-primary-button-background, #4f46e5)"
        : "transparent"};
  background: none;
  padding: 2px;
  transition: background-color 0.15s;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "none" : "var(--color-card-text-button-hover-background, #f3f4f6)"};
  }
`;

const AdvancedErrorText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #dc2626;
`;

/** Figma caret asset (2602:1721 "Icon/arrow"): 5x8 chevron, rendered pointing down. */
const CaretDownIcon: React.FC = () => (
  <CaretWrap>
    <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.70711 0.292893C8.09763 0.683418 8.09763 1.31658 7.70711 1.70711L4.70711 4.70711C4.31658 5.09763 3.68342 5.09763 3.29289 4.70711L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683418 0.292893 0.292893C0.683418 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L4 2.58579L6.29289 0.292893C6.68342 -0.0976311 7.31658 -0.0976311 7.70711 0.292893Z"
        fill="currentColor"
      />
    </svg>
  </CaretWrap>
);

/** Figma info glyph (node 3193:5202): 13.33px circle-i inset in a 16px box. */
const InfoOutlineIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(1.3333 1.3333)">
      <path
        d="M6 3.33333H7.33333V4.66667H6V3.33333ZM6 6H7.33333V10H6V6ZM6.66667 0C2.98667 0 0 2.98667 0 6.66667C0 10.3467 2.98667 13.3333 6.66667 13.3333C10.3467 13.3333 13.3333 10.3467 13.3333 6.66667C13.3333 2.98667 10.3467 0 6.66667 0ZM6.66667 12C3.72667 12 1.33333 9.60667 1.33333 6.66667C1.33333 3.72667 3.72667 1.33333 6.66667 1.33333C9.60667 1.33333 12 3.72667 12 6.66667C12 9.60667 9.60667 12 6.66667 12Z"
        fill="currentColor"
      />
    </g>
  </svg>
);

/** Figma upload glyph (noun-upload-5416341, node 3193:5380): tray + up arrow, 16px. */
const UploadGlyphIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 15.3129 15.9997"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M15.2688 12.6253C15.3665 12.3075 15.2996 11.9618 15.0901 11.7033C14.8808 11.445 14.5566 11.3078 14.2253 11.3376C13.894 11.3673 13.5993 11.56 13.4394 11.8516C13.0316 12.8429 12.1442 13.5561 11.0884 13.7409C9.95543 13.9167 8.81056 14.0044 7.66416 14.003C6.5305 14.006 5.39821 13.9226 4.2774 13.7534C3.21865 13.5814 2.3214 12.8799 1.89893 11.894C1.81037 11.6911 1.6569 11.5234 1.46265 11.4172C1.2684 11.311 1.04433 11.2724 0.825704 11.3075C0.526981 11.3599 0.268294 11.5454 0.122624 11.8114C-0.0230438 12.0775 -0.03978 12.3953 0.0769661 12.6753C0.415663 13.4651 0.947408 14.1571 1.62335 14.6879C2.29929 15.2186 3.09783 15.5709 3.94546 15.7126C5.17587 15.9031 6.41918 15.9991 7.66416 15.9997C8.90757 15.9988 10.1491 15.9029 11.3779 15.7126C12.2334 15.5659 13.0383 15.2062 13.7183 14.6667C14.3982 14.1272 14.9315 13.4252 15.2688 12.6253Z"
      fill="currentColor"
    />
    <path
      d="M11.9493 4.83515C12.2025 4.6712 12.368 4.40147 12.3995 4.10146C12.431 3.80142 12.3251 3.50326 12.1115 3.29025L9.68564 0.864355C9.39617 0.569042 9.04747 0.338557 8.6624 0.187938C8.15536 -0.0075314 7.60259 -0.0516565 7.07093 0.0608738C6.53935 0.173396 6.05182 0.437756 5.66747 0.821877L3.19665 3.2902C2.9864 3.49988 2.88038 3.79228 2.90735 4.08799C2.93441 4.38372 3.09161 4.65206 3.33648 4.82015C3.54113 4.95639 3.78755 5.01537 4.03176 4.98653C4.27589 4.95777 4.50184 4.84298 4.66918 4.66287L6.6658 2.66625V9.99634C6.6658 10.353 6.85607 10.6826 7.16495 10.8609C7.47385 11.0392 7.85437 11.0392 8.16326 10.8609C8.47216 10.6826 8.66242 10.353 8.66242 9.99634V2.66376L10.684 4.68531C10.8459 4.85129 11.0599 4.95667 11.2902 4.98395C11.5204 5.01117 11.7531 4.9586 11.9493 4.83503Z"
      fill="currentColor"
    />
  </svg>
);

/** Figma delete glyph (node "Delete", 3193:5384): rotated trash can, 16px inside a round button. */
const DeleteGlyphSvg = styled.svg`
  transform: rotate(90deg);
`;

const DeleteGlyphIcon: React.FC = () => (
  <DeleteGlyphSvg
    width="16"
    height="16"
    viewBox="8 8 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.75 8.99989L11.75 12.3274C11.59 12.3267 11.4306 12.3451 11.275 12.3824L9.90493 12.7174L9.90502 12.7175C9.36123 12.8507 8.87791 13.1626 8.53254 13.6032C8.18708 14.0438 7.99958 14.5876 8 15.1474L8 16.8525C8.00016 17.4119 8.18791 17.9551 8.53329 18.3952C8.87866 18.8352 9.36166 19.1468 9.90504 19.28L11.275 19.6175C11.4306 19.6547 11.59 19.6732 11.75 19.6725L11.75 23C11.75 23.3572 11.9406 23.6874 12.25 23.866C12.5594 24.0447 12.9406 24.0447 13.25 23.866C13.5594 23.6874 13.75 23.3572 13.75 23L13.75 22.265L14 22.2325L17 21.8525L19 21.6175L21.5875 21.3125C22.2535 21.2301 22.8663 20.9071 23.3107 20.4043C23.755 19.9014 24.0002 19.2535 24 18.5825L24 13.4175C23.9998 12.7442 23.7524 12.0943 23.3051 11.5912C22.8577 11.088 22.2412 10.7664 21.5725 10.6875L18.985 10.3825L16.985 10.1475L13.985 9.76751L13.735 9.73504L13.735 9.00001C13.735 8.64276 13.5445 8.3126 13.235 8.13397C12.9256 7.95534 12.5445 7.95534 12.2351 8.13397C11.9256 8.3126 11.7351 8.64276 11.7351 9.00001L11.75 8.99989ZM10.38 17.3399C10.2711 17.313 10.1746 17.2503 10.1056 17.1619C10.0367 17.0735 9.9995 16.9645 9.99999 16.8524L9.99999 15.1449C10.0001 15.0333 10.0375 14.9248 10.1064 14.8369C10.1753 14.749 10.2716 14.6867 10.38 14.6599L11.75 14.3249L11.75 17.6749L10.38 17.3399ZM16.7499 12.1324L18.7499 12.3674L21.3374 12.6724C21.5209 12.6939 21.6898 12.7824 21.812 12.9207C21.9343 13.0592 22.0012 13.2378 21.9999 13.4224L21.9999 18.5825C22.0012 18.7671 21.9343 18.9457 21.812 19.0841C21.6898 19.2224 21.5208 19.3109 21.3374 19.3325L18.7499 19.6324L16.7499 19.8674L13.7499 20.2499L13.7499 11.7499L16.7499 12.1324Z"
      fill="currentColor"
    />
  </DeleteGlyphSvg>
);

const LOGO_FILE_ACCEPT = ".png,.svg,image/png,image/svg+xml";
const LOGO_MAX_BYTES = 1024 * 1024; // mirrors the backend's 1 MB cap

/**
 * One white-label logo slot in the Advanced section (Figma 3193:5348 empty / 3193:5185 filled).
 * Empty: dashed drop target ("Drag here, or choose file"). Filled: the artwork previewed on the
 * background it is designed for (white / near-black), with round Upload + Delete actions shown
 * on hover (Figma "Hover state", 3193:5369).
 */
const LogoUploadBox: React.FC<{
  label: string;
  /** Which BACKGROUND the artwork is designed for (matches the backend naming). */
  variant: "light" | "dark";
  /** Whether the selector PANEL is in dark mode (drives the empty drop target's colors). */
  isDarkPanel: boolean;
  logoUrl?: string | null;
  alt: string;
  isBusy: boolean;
  onFile: (file: File) => void;
  onDelete: () => void;
}> = ({ label, variant, isDarkPanel, logoUrl, alt, isBusy, onFile, onDelete }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const pickFile = () => inputRef.current?.click();

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onFile(file);
    }
  };

  return (
    <LogoSlot>
      <LogoSlotLabel>{label}</LogoSlotLabel>
      <HiddenFileInput
        ref={inputRef}
        type="file"
        accept={LOGO_FILE_ACCEPT}
        onChange={event => {
          const file = event.target.files?.[0];
          if (file) {
            onFile(file);
          }
          // Allow re-selecting the same file after a failed upload.
          event.target.value = "";
        }}
      />
      {logoUrl ? (
        <LogoPreviewBox
          $darkArtworkBackground={variant === "dark"}
          onDragOver={event => event.preventDefault()}
          onDrop={handleDrop}
        >
          <LogoImg src={logoUrl} alt={alt} />
          <LogoOverlay>
            <RoundIconButton
              type="button"
              aria-label={`Replace ${label}`}
              onClick={pickFile}
              disabled={isBusy}
            >
              <UploadGlyphIcon />
            </RoundIconButton>
            <RoundIconButton
              type="button"
              aria-label={`Remove ${label}`}
              onClick={onDelete}
              disabled={isBusy}
            >
              <DeleteGlyphIcon />
            </RoundIconButton>
          </LogoOverlay>
        </LogoPreviewBox>
      ) : (
        <LogoDropButton
          type="button"
          $dark={isDarkPanel}
          onClick={pickFile}
          onDragOver={event => event.preventDefault()}
          onDrop={handleDrop}
          disabled={isBusy}
        >
          <DropText $dark={isDarkPanel}>
            {isBusy ? (
              "Uploading…"
            ) : (
              <>
                Drag here, or <ChooseFileText>choose file</ChooseFileText>
              </>
            )}
          </DropText>
        </LogoDropButton>
      )}
    </LogoSlot>
  );
};

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({
  themeId,
  mode,
  background,
  handleThemeChange,
  targetDocument = document,
  isOpen,
  setIsOpen,
  onRequestTheme,
  onPreviewTheme,
  onThemeCommitted,
  onThemeUncommitted,
  onThemeDeleted,
  onThemeDeleteFailed,
  onEditTheme,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Gates only the "Custom themes" SECTION (list/create); the Advanced section has its own flag
  // below — a domain admin can set a built-in palette as the domain default with custom themes
  // unlaunched.
  const { value: isCustomThemesEnabled } = useFeatureGate(
    FeatureFlag.EnableWebDashboardCustomThemeCreation
  );

  // Gates the whole Advanced section (domain logo upload + domain default theme); the backend
  // enforces the same gate on the logo endpoints.
  const { value: isLogoAdminEnabled } = useFeatureGate(
    FeatureFlag.EnableDashboardLogoUploadByAdmin
  );

  // The user's custom themes (newest first) + domain branding state (can_set_default,
  // default_theme_id, custom_logo). Skipped only when both gated consumers are off. The selector
  // only mounts while open and only in editing mode, so this fetches lazily per open
  // (react-query caches across opens).
  const { data: customThemesData } = useWebDashboardCustomThemes({
    enabled: isCustomThemesEnabled || isLogoAdminEnabled,
  });

  const customThemes = customThemesData?.themes ?? [];

  // The list can include the domain-default theme owned by another user (is_owner === false);
  // it renders among the custom themes with Edit/Delete disabled. Only owned themes are valid
  // picks in the Advanced default-theme picker.
  const ownedCustomThemes = customThemes.filter(summary => summary.is_owner !== false);

  const deleteTheme = useDeleteWebDashboardCustomTheme();

  // Advanced section (Figma "Logo & Default theme", enterprise/domain admins only): domain
  // white-label logos + the domain default theme for new dashboards. The backend authorizes both
  // (can_set_default mirrors that gate), so non-admins never see the section.
  const canManageDomainTheme = !!customThemesData?.can_set_default;

  const customLogo = customThemesData?.custom_logo;

  const defaultThemeId = customThemesData?.default_theme_id ?? null;

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const [isDefaultPickerOpen, setIsDefaultPickerOpen] = useState(false);

  // Viewport (iframe) coords for the fixed-positioned picker, measured from the trigger on open.
  const [pickerPosition, setPickerPosition] = useState<{
    left: number;
    width: number;
    top?: number;
    bottom?: number;
  } | null>(null);

  const [advancedError, setAdvancedError] = useState<string | null>(null);

  const setCustomLogo = useSetWebDashboardCustomLogo();

  const deleteCustomLogo = useDeleteWebDashboardCustomLogo();

  const setDefaultTheme = useSetWebDashboardCustomThemeDefault();

  const isLogoBusy = setCustomLogo.isLoading || deleteCustomLogo.isLoading;

  const handleLogoFile = async (variant: "light" | "dark", file: File) => {
    setAdvancedError(null);
    if (file.size > LOGO_MAX_BYTES) {
      setAdvancedError("Logo must be 1 MB or smaller.");
      return;
    }
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      await setCustomLogo.mutateAsync({
        variant,
        imageBase64: dataUrl.slice(dataUrl.indexOf(",") + 1),
      });
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      setAdvancedError(message || "Upload failed. PNG or SVG only, at least 64px tall.");
    }
  };

  const handleLogoDelete = async (variant: "light" | "dark") => {
    setAdvancedError(null);
    try {
      await deleteCustomLogo.mutateAsync(variant);
    } catch {
      setAdvancedError("Couldn't remove the logo. Please try again.");
    }
  };

  const handlePickDefaultTheme = async (pickedThemeId: string, isDefault: boolean) => {
    setIsDefaultPickerOpen(false);
    setAdvancedError(null);
    try {
      await setDefaultTheme.mutateAsync({ themeId: pickedThemeId, isDefault });
    } catch {
      setAdvancedError("Couldn't update the default theme. Please try again.");
    }
  };

  // Kebab actions menu: which theme's menu is open, and whether it is showing the delete
  // confirmation. A theme is shared across dashboards, so Delete asks before it lands.
  const [actionMenuThemeId, setActionMenuThemeId] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const closeActionMenu = () => {
    setActionMenuThemeId(null);
    setIsConfirmingDelete(false);
    setIsDefaultPickerOpen(false);
  };

  // Register each spec so `custom:{uuid}` ids expand in resolveTheme the moment a pill is
  // clicked for live preview. Registration is idempotent (re-registering invalidates the cache).
  useEffect(() => {
    for (const summary of customThemes) {
      // Cast: the wire type widens mode/background.type to string; the stored values are the spec's.
      registerCustomThemeSpec(
        `${CUSTOM_THEME_ID_PREFIX}${summary.theme_id}`,
        summary.theme as ThemeSpec
      );
    }
  }, [customThemes]);

  // Warm the browser cache while Advanced is still collapsed, so expanding it doesn't flash
  // empty preview boxes while the logos download.
  useEffect(() => {
    for (const url of [customLogo?.light_logo_url, customLogo?.dark_logo_url]) {
      if (url) {
        new Image().src = url;
      }
    }
  }, [customLogo?.light_logo_url, customLogo?.dark_logo_url]);

  // Snapshot of the theme state when the dropdown opened. Palette/toggle clicks apply as LOCAL
  // live previews (no backend writes while browsing); every exit keeps the changes and persists
  // them once, and a dirty session hands this snapshot to the parent's "Theme updated" toast,
  // whose Reset action is the ONLY way to revert to it. Re-captured on every open; a ref holds
  // the live values so the snapshot effect only fires on the open transition.
  const liveThemeState = useRef({ themeId, mode, background });
  liveThemeState.current = { themeId, mode, background };
  const [openSnapshot, setOpenSnapshot] = useState<{
    themeId: string;
    mode: string;
    background: string;
  } | null>(null);
  const modeTouchedRef = useRef(false);
  const saveThemeModePreference = useSaveWebDashboardThemeModePreference();
  // Whether THIS browse raised the Reset toast. A toast left over from an earlier session (e.g. a
  // Done that persisted) reverts to an older theme and is not ours to dismiss.
  const raisedToastRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      setOpenSnapshot(liveThemeState.current);
      raisedToastRef.current = false;
      modeTouchedRef.current = false;
    }
  }, [isOpen]);

  const isDirty =
    !!openSnapshot &&
    (openSnapshot.themeId !== themeId ||
      openSnapshot.mode !== mode ||
      openSnapshot.background !== background);

  // Palette/toggle clicks are LIVE PREVIEWS (local state only, no backend write); the session's
  // final state persists once in closeSelector.
  const previewChange = onPreviewTheme ?? handleThemeChange;

  /**
   * Apply a palette/toggle click and surface the Reset affordance right away: the change is already
   * visible, so waiting for Done to offer Reset leaves the user with no way back. A click that
   * lands back on the state the dropdown opened with retracts a toast THIS browse raised — its
   * Reset now targets the current theme — while leaving an earlier session's toast alone.
   */
  const applyChange = (next: { themeId?: string; mode?: string; background?: string }) => {
    previewChange(next);
    if (!openSnapshot) {
      return;
    }
    const merged = { themeId, mode, background, ...next };
    const divergesFromOpen =
      openSnapshot.themeId !== merged.themeId ||
      openSnapshot.mode !== merged.mode ||
      openSnapshot.background !== merged.background;
    if (divergesFromOpen) {
      raisedToastRef.current = true;
      onThemeCommitted?.(openSnapshot);
    } else if (raisedToastRef.current) {
      raisedToastRef.current = false;
      onThemeUncommitted?.();
    }
  };

  /**
   * Close the selector, keeping the changes. A dirty session persists ONCE here (one PATCH, one
   * version row, one undo entry for the whole browse) and hands the parent the snapshot for the
   * Reset toast.
   */
  const sessionClosedRef = useRef(false);
  const closeSelector = () => {
    if (sessionClosedRef.current) {
      return;
    }
    sessionClosedRef.current = true;
    if (isDirty && openSnapshot) {
      handleThemeChange({ themeId, mode, background });
      onThemeCommitted?.(openSnapshot);
    }
    if (
      modeTouchedRef.current &&
      isToggleAwareThemeId(themeId) &&
      (mode === "light" || mode === "dark")
    ) {
      saveThemeModePreference.mutate(mode);
    }
    setIsOpen(false);
  };

  // Latest closeSelector for the outside-click listener (registered once per open/document).
  const closeSelectorRef = useRef(closeSelector);
  closeSelectorRef.current = closeSelector;

  // Reset the session guard whenever the selector (re)opens.
  useEffect(() => {
    if (isOpen) {
      sessionClosedRef.current = false;
    }
  }, [isOpen]);

  // Safety net: the selector can be closed externally (e.g. the nav backdrop click flips
  // setIsThemeSelectorOpen directly). This component only renders while open, so unmount == close:
  // commit any un-persisted previewed changes rather than silently dropping them on reload.
  useEffect(() => {
    return () => closeSelectorRef.current();
  }, []);

  // Clicking outside closes like the Done/close buttons (changes kept, Reset toast if dirty).
  // MOUSEDOWN, not click: the whole nav is portaled into the iframe document, so the trigger and
  // this listener share a document. A `click` listener attached while the OPENING click is still
  // bubbling toward the document can fire for that same click (React may flush the mount's
  // effects in a microtask between listeners) and instantly close the just-opened selector —
  // sequence-dependent, so it reads as "the theme menu stopped opening". The opening interaction's
  // mousedown happened before this listener existed, so mousedown can never see it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeSelectorRef.current();
      }
    };

    targetDocument.addEventListener("mousedown", handleClickOutside);
    return () => targetDocument.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, targetDocument]);

  const handleSelect = (newThemeId: string) => {
    applyChange({ themeId: newThemeId });
  };

  /**
   * Delete a custom theme (already confirmed by the caller). The Undo toast only goes up once the
   * soft-delete has landed, so its
   * restore can never race ahead of the delete it undoes, and a failed delete reports failure
   * instead of a phantom "Deleted" toast.
   *
   * Deleting the APPLIED theme flips the dashboard to the default palette. That flip persists
   * right here rather than through the browse/Reset path: the delete toast's Undo is the only
   * restore route, so it must not be replaced by a "Theme updated" toast on close, and that
   * toast's Reset would point at the theme we just deleted. Re-baselining the open snapshot
   * leaves the rest of the session behaving normally, with Reset targeting the default palette.
   */
  const handleDelete = async (deletedThemeId: string, label: string, isApplied: boolean) => {
    try {
      await deleteTheme.mutateAsync(deletedThemeId);
    } catch {
      onThemeDeleteFailed?.();
      return;
    }
    if (isApplied) {
      const next = { themeId: DEFAULT_THEME_ID, mode, background };
      handleThemeChange(next);
      setOpenSnapshot(next);
      raisedToastRef.current = false;
    }
    onThemeDeleted?.({ themeId: deletedThemeId, name: label });
  };

  if (!isOpen) {
    return null;
  }

  const isDark = mode === "dark";

  return (
    // StyleSheetManager: the selector is portaled into the dashboard IFRAME (targetDocument).
    // styled-components injects its <style> tags into the parent document's head by default, so
    // without retargeting, none of the styled CSS reaches the iframe and the section renders as
    // bare text.
    <StyleSheetManager target={targetDocument.head}>
      {/* Figma "Theme" dropdown (latest: 3193:4917): card surface, 12px radius, soft shadow — drops
        below the trigger (top-full) and stacks above content (z-50). Uses the themed card tokens so
        the panel re-colors with the active palette + mode. 440px wide = 2 cells of
        (166px pill + 8px + 24px kebab slot) + 12px column gap + 16px side paddings. */}
      <div
        ref={dropdownRef}
        onClick={closeActionMenu}
        className="absolute right-0 top-full z-50 mt-2 flex max-h-[calc(100vh-8rem)] w-[440px] flex-col gap-5 overflow-y-auto rounded-[12px] border border-card-border bg-card-background px-4 pb-4 pt-2 text-card-foreground shadow-lg"
      >
        {/* Header: palette icon + title + close */}
        <div className="flex h-6 items-center gap-1.5">
          <ThemePaletteIcon width="16" height="16" fill="currentColor" />
          <span className="flex-1 text-sm font-semibold leading-[18px]">Theme</span>
          <button
            type="button"
            onClick={closeSelector}
            aria-label="Close theme menu"
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-card-muted-foreground transition-colors hover:bg-card-text-button-hover-background hover:text-card-foreground"
          >
            <CloseIcon width="12" height="12" />
          </button>
        </div>

        {/* Palettes. No section heading per the latest mocks — the panel title carries the label.
          Legacy-theme dashboards highlight their closest mapped palette. */}
        <div className="flex w-full flex-col gap-2">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {BUILTIN_PALETTES.map(palette => {
              const isSelected =
                palette.id === themeId ||
                LEGACY_THEME_TO_PALETTE[themeId]?.paletteId === palette.id;
              return (
                <div key={palette.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleSelect(palette.id)}
                    aria-pressed={isSelected}
                    className={
                      // Figma pill: 32px tall = 24px colors + 2.5px padding + 1.5px ring. The ring is
                      // a border so unselected pills keep identical geometry.
                      "flex w-full min-w-0 cursor-pointer items-center rounded-[10px] border-2 p-[2px] outline-none transition-colors focus:outline-none focus-visible:outline-none " +
                      (isSelected
                        ? // Figma selection ring: the theme primary in light mode, white in dark.
                          isDark
                          ? "border-white"
                          : "border-primary-button-background"
                        : "border-transparent hover:bg-card-text-button-hover-background")
                    }
                  >
                    <div className="flex h-6 w-full gap-[2px] overflow-hidden rounded-lg">
                      {palette.colors.map((color, i) => (
                        <div key={i} className="h-full flex-1" style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  </button>
                  {/* Empty kebab slot keeps built-in pills the same width as custom ones. */}
                  <div className="w-6 shrink-0" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Custom themes: same pill treatment as the built-ins, plus a hover kebab with actions
          in the reserved slot next to the pill (Figma "Hover Icon"). The list can include the
          domain-default theme owned by another user (a domain admin) — it renders like the rest
          but with Edit/Delete disabled. "+ Custom theme" is the last grid cell and opens the
          theme request modal. Gated off entirely until custom themes launch — with the flag off,
          the footer "Request custom theme" link is the entry point instead. */}
        {isCustomThemesEnabled && (
          <div className="flex w-full flex-col gap-2">
            <span className="text-sm font-semibold leading-[18px]">Custom themes</span>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {customThemes.map((summary, index) => {
                const isOwned = summary.is_owner !== false;
                // Extracted brand names can be odd; the UI labels owned themes by position
                // instead. The non-owned entry is the domain default set by an admin.
                const themeLabel = isOwned
                  ? `Custom theme ${ownedCustomThemes.indexOf(summary) + 1}`
                  : "Default theme";
                const customThemeId = `${CUSTOM_THEME_ID_PREFIX}${summary.theme_id}`;
                const isSelected = customThemeId === themeId;
                const isMenuOpen = actionMenuThemeId === summary.theme_id;

                const actionItemClassName =
                  "flex h-6 items-center gap-2 text-left text-sm font-medium " +
                  (isOwned ? "cursor-pointer" : "cursor-default text-card-muted-foreground/60");
                return (
                  <div key={customThemeId} className="group relative flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleSelect(customThemeId)}
                      aria-pressed={isSelected}
                      title={themeLabel}
                      className={
                        "flex w-full min-w-0 cursor-pointer items-center rounded-[10px] border-2 p-[2px] outline-none transition-colors focus:outline-none focus-visible:outline-none " +
                        (isSelected
                          ? isDark
                            ? "border-white"
                            : "border-primary-button-background"
                          : "border-transparent hover:bg-card-text-button-hover-background")
                      }
                    >
                      <div className="flex h-6 w-full gap-[2px] overflow-hidden rounded-lg">
                        {summary.theme.colors.map((color, i) => (
                          <div
                            key={i}
                            className="h-full flex-1"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </button>
                    {/* Kebab in the reserved slot: visible on hover, or while its menu is open. */}
                    <div className="flex w-6 shrink-0 items-center justify-center">
                      <button
                        type="button"
                        aria-label={`Theme actions for ${themeLabel}`}
                        onClick={event => {
                          event.stopPropagation();
                          setIsConfirmingDelete(false);
                          setActionMenuThemeId(isMenuOpen ? null : summary.theme_id ?? null);
                        }}
                        className={
                          "flex cursor-pointer items-center justify-center rounded p-0.5 text-card-muted-foreground hover:text-card-foreground " +
                          (isMenuOpen ? "visible" : "invisible group-hover:visible")
                        }
                      >
                        <Ellipsis width="4" height="16" />
                      </button>
                    </div>
                    {/* Actions menu (Figma 3071:55988 / 3071:56002). Delete only — the domain default
                    moved out of this dropdown in the latest designs (its API is still live). */}
                    {/* Figma 3071:55988: compact borderless white panel, 12px radius, soft double
                    shadow, 24px rows with a 24px icon slot + 13px medium labels. */}
                    {isMenuOpen && (
                      <div
                        // Figma clicked-state geometry: panel 115px wide, top 7px below the pill row,
                        // right edge tucked 18px in from the cell edge (under the kebab's left side).
                        // The confirmation step needs prose width, so it widens out of that geometry.
                        className={
                          "group/menu absolute right-[18px] top-full z-10 mt-[7px] flex flex-col rounded-[12px] bg-card-background text-card-foreground shadow-[0px_0px_8px_rgba(0,0,0,0.08),0px_16px_12px_rgba(0,0,0,0.08)] " +
                          (isConfirmingDelete
                            ? "min-w-[190px] max-w-[240px] py-1"
                            : "min-w-[115px] gap-3 py-2 pl-2 pr-4")
                        }
                        onClick={event => event.stopPropagation()}
                      >
                        {isConfirmingDelete ? (
                          // A custom theme belongs to the user, not to this dashboard: deleting it
                          // strips it from every dashboard it is applied to, so say that before the
                          // delete lands rather than leaving the Undo toast to carry the warning.
                          <div className="flex flex-col gap-2 px-3 py-2">
                            <p className="text-xs leading-[16px] text-card-muted-foreground">
                              Delete this theme? It will be removed from every dashboard using it.
                            </p>
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                className="cursor-pointer rounded-md px-2 py-1 text-xs font-semibold text-card-muted-foreground hover:bg-card-text-button-hover-background"
                                onClick={closeActionMenu}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="cursor-pointer rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700"
                                onClick={() => {
                                  closeActionMenu();
                                  void handleDelete(summary.theme_id, themeLabel, isSelected);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Non-owned (domain default set by an admin): actions stay visible
                            but greyed and disabled, with a hover tooltip naming who to contact. */}
                            {onEditTheme && (
                              <button
                                type="button"
                                disabled={!isOwned}
                                className={actionItemClassName}
                                onClick={() => {
                                  closeActionMenu();
                                  closeSelector();
                                  onEditTheme({
                                    themeId: summary.theme_id ?? "",
                                    colors: summary.theme.colors ?? [],
                                  });
                                }}
                              >
                                <span className="flex size-6 shrink-0 items-center justify-center">
                                  <PencilIcon width="14" height="14" fill="currentColor" />
                                </span>
                                Edit
                              </button>
                            )}
                            {/* Confirmed deletes apply immediately; the parent's toast offers Undo. */}
                            <button
                              type="button"
                              disabled={!isOwned}
                              className={actionItemClassName}
                              onClick={() => setIsConfirmingDelete(true)}
                            >
                              <span className="flex size-6 shrink-0 items-center justify-center">
                                <DeleteIcon width="16" height="16" fill="currentColor" />
                              </span>
                              Delete
                            </button>
                            {/* Hover tooltip naming who to contact. Always white like
                            InfoTooltip — the design has no dark spec. Anchored ABOVE the menu
                            (the header + palette rows above always give it room, while below the
                            last row it could overflow into unreachable hover-lost scroll space)
                            and aligned by grid column (left column opens rightward, right column
                            leftward) so it always stays inside the panel. */}
                            {!isOwned && (
                              <div
                                className={
                                  "pointer-events-none absolute bottom-full z-20 mb-1 hidden w-[240px] rounded-md bg-white p-1.5 text-sm font-medium leading-[18px] text-[#111215] shadow-[0px_0px_8px_rgba(0,0,0,0.08),0px_16px_12px_rgba(0,0,0,0.08)] group-hover/menu:block " +
                                  (index % 2 === 0 ? "left-0" : "right-0")
                                }
                              >
                                Contact your admin
                                {summary.owner_email ? ` (${summary.owner_email})` : ""} to edit or
                                delete your default theme.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* "+ Custom" entry point, last grid cell (Figma 3071:55801): shaped exactly like a
              palette pill — 24px neutral-gray inner cell inside the same 32px ring geometry.
              card-foreground/10 ≈ the Figma #eaeaea on a white card and stays neutral in dark
              mode. Closes like any other exit. */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    closeSelector();
                    onRequestTheme();
                  }}
                  className="flex w-full min-w-0 cursor-pointer items-center rounded-[10px] border-2 border-transparent p-[2px] outline-none transition-colors focus:outline-none focus-visible:outline-none hover:bg-card-text-button-hover-background"
                >
                  <div className="flex h-6 w-full items-center justify-center rounded-lg bg-card-foreground/10 text-xs font-medium">
                    + Custom
                  </div>
                </button>
                <div className="w-6 shrink-0" />
              </div>
            </div>
          </div>
        )}

        {/* Page-background + Dark-mode toggles. Controlled off live props so they stay in
          sync with persisted values after reload. */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <ToggleSwitch
              ariaLabel="Page background"
              offTrackClassName="bg-main-muted-foreground/40"
              checked={background === "on"}
              onChange={checked => applyChange({ background: checked ? "on" : "off" })}
            />
            <span className="text-sm font-medium leading-[18px]">Page background</span>
          </div>
          <div className="flex items-center gap-2">
            <ToggleSwitch
              ariaLabel="Dark mode"
              offTrackClassName="bg-main-muted-foreground/40"
              checked={mode === "dark"}
              onChange={checked => {
                modeTouchedRef.current = true;
                applyChange({ mode: checked ? "dark" : "light" });
              }}
            />
            <span className="text-sm font-medium leading-[18px]">Dark mode</span>
          </div>
        </div>

        {/* Advanced (Figma "Logo & Default theme (Enterprise admins only)", 3193:5343): domain
          white-label logos + the domain default theme for new dashboards. Shown to domain admins
          (can_set_default) behind its launch flag; the backend enforces both gates on every
          write. Independent of the custom-themes flag — built-in palettes are valid domain
          defaults too. */}
        {isLogoAdminEnabled && canManageDomainTheme && (
          <AdvancedSection $dark={isDark}>
            <AdvancedHeaderButton
              type="button"
              onClick={() => setIsAdvancedOpen(open => !open)}
              aria-expanded={isAdvancedOpen}
            >
              Advanced
              <CaretDownIcon />
            </AdvancedHeaderButton>
            {isAdvancedOpen && (
              <>
                <LogoRow>
                  <LogoUploadBox
                    label={customLogo?.light_logo_url ? "Logo" : "Upload logo"}
                    variant="light"
                    isDarkPanel={isDark}
                    logoUrl={customLogo?.light_logo_url}
                    alt={customLogo?.alt ?? "Company logo"}
                    isBusy={isLogoBusy}
                    onFile={file => void handleLogoFile("light", file)}
                    onDelete={() => void handleLogoDelete("light")}
                  />
                  <LogoUploadBox
                    label="Logo (dark mode)"
                    variant="dark"
                    isDarkPanel={isDark}
                    logoUrl={customLogo?.dark_logo_url}
                    alt={customLogo?.alt ?? "Company logo"}
                    isBusy={isLogoBusy}
                    onFile={file => void handleLogoFile("dark", file)}
                    onDelete={() => void handleLogoDelete("dark")}
                  />
                </LogoRow>

                <DefaultThemeBlock>
                  <DefaultThemeLabelRow>
                    Default theme
                    {/* Info tooltip (Figma 3193:5401): white card above the icon, hover-only. */}
                    <InfoWrap>
                      <InfoOutlineIcon />
                      <InfoTooltip>
                        Default theme will apply to all new dashboards for all users on your domain.
                        Users can change themes, post creation.
                      </InfoTooltip>
                    </InfoWrap>
                  </DefaultThemeLabelRow>
                  {(() => {
                    // The default is a built-in `palette:*` id or a custom theme UUID. A custom
                    // default owned by another admin is included in the list (is_owner=false),
                    // so its colors render here too.
                    const defaultBuiltin = defaultThemeId
                      ? BUILTIN_PALETTES.find(palette => palette.id === defaultThemeId)
                      : undefined;

                    const defaultCustom =
                      defaultThemeId && !defaultBuiltin
                        ? customThemes.find(summary => summary.theme_id === defaultThemeId)
                        : undefined;

                    const defaultColors = defaultBuiltin?.colors ?? defaultCustom?.theme.colors;

                    return (
                      <PickerWrap>
                        <PickerTrigger
                          type="button"
                          aria-haspopup="listbox"
                          aria-expanded={isDefaultPickerOpen}
                          onClick={event => {
                            event.stopPropagation();
                            closeActionMenu();
                            const rect = event.currentTarget.getBoundingClientRect();
                            const viewportHeight = (targetDocument.defaultView ?? window)
                              .innerHeight;
                            const needed = PICKER_MAX_HEIGHT + PICKER_TRIGGER_GAP;
                            setPickerPosition(
                              viewportHeight - rect.bottom >= needed
                                ? {
                                    left: rect.left,
                                    width: rect.width,
                                    top: rect.bottom + PICKER_TRIGGER_GAP,
                                  }
                                : {
                                    left: rect.left,
                                    width: rect.width,
                                    bottom: viewportHeight - rect.top + PICKER_TRIGGER_GAP,
                                  }
                            );
                            setIsDefaultPickerOpen(!isDefaultPickerOpen);
                          }}
                        >
                          {defaultColors ? (
                            <>
                              <ColorStrip>
                                {defaultColors.map((color, i) => (
                                  <ColorCell key={i} style={{ backgroundColor: color }} />
                                ))}
                              </ColorStrip>
                              <CaretDownIcon />
                            </>
                          ) : (
                            <ChooseThemePill $dark={isDark}>No default theme</ChooseThemePill>
                          )}
                        </PickerTrigger>
                        {/* Picker (Figma 3428:5778): "No default theme" + built-in palettes, then
                          the caller's custom themes when the custom-themes flag is on (the
                          backend accepts both as the default). */}
                        {isDefaultPickerOpen && pickerPosition && (
                          <PickerPanel
                            $dark={isDark}
                            style={pickerPosition}
                            onClick={event => event.stopPropagation()}
                          >
                            <PickerHeader>Choose default...</PickerHeader>
                            <PickerOption
                              type="button"
                              onClick={() =>
                                defaultThemeId
                                  ? void handlePickDefaultTheme(defaultThemeId, false)
                                  : setIsDefaultPickerOpen(false)
                              }
                              aria-pressed={!defaultThemeId}
                              $selected={false}
                              $darkMode={isDark}
                            >
                              <NoDefaultPill $dark={isDark} $selected={!defaultThemeId}>
                                No default theme
                              </NoDefaultPill>
                            </PickerOption>
                            {BUILTIN_PALETTES.map(palette => {
                              const isCurrentDefault = palette.id === defaultThemeId;
                              return (
                                <PickerOption
                                  key={palette.id}
                                  type="button"
                                  onClick={() => void handlePickDefaultTheme(palette.id, true)}
                                  aria-pressed={isCurrentDefault}
                                  $selected={isCurrentDefault}
                                  $darkMode={isDark}
                                  title={palette.name}
                                >
                                  <ColorStrip>
                                    {palette.colors.map((color, i) => (
                                      <ColorCell key={i} style={{ backgroundColor: color }} />
                                    ))}
                                  </ColorStrip>
                                </PickerOption>
                              );
                            })}
                            {/* Owned themes only: the set-default API rejects another user's
                              theme (a non-owned entry here is the current domain default). */}
                            {isCustomThemesEnabled && ownedCustomThemes.length > 0 && (
                              <>
                                <PickerSectionLabel>Custom themes</PickerSectionLabel>
                                {ownedCustomThemes.map(summary => {
                                  const isCurrentDefault = summary.theme_id === defaultThemeId;
                                  return (
                                    <PickerOption
                                      key={summary.theme_id}
                                      type="button"
                                      onClick={() =>
                                        void handlePickDefaultTheme(summary.theme_id, true)
                                      }
                                      aria-pressed={isCurrentDefault}
                                      $selected={isCurrentDefault}
                                      $darkMode={isDark}
                                    >
                                      <ColorStrip>
                                        {summary.theme.colors.map((color, i) => (
                                          <ColorCell key={i} style={{ backgroundColor: color }} />
                                        ))}
                                      </ColorStrip>
                                    </PickerOption>
                                  );
                                })}
                              </>
                            )}
                          </PickerPanel>
                        )}
                      </PickerWrap>
                    );
                  })()}
                </DefaultThemeBlock>

                {advancedError && <AdvancedErrorText>{advancedError}</AdvancedErrorText>}
              </>
            )}
          </AdvancedSection>
        )}

        {/* Footer (Figma 3193:5049): Done only. With custom themes unlaunched there is no
            "+ Custom" pill above, so the request link stays as the only entry point. */}
        <div className="flex w-full items-center justify-end gap-6">
          {!isCustomThemesEnabled && (
            <div className="flex-1">
              <button
                type="button"
                onClick={() => {
                  closeSelector();
                  onRequestTheme();
                }}
                className="cursor-pointer text-xs font-semibold leading-[18px] text-card-muted-foreground underline"
              >
                Request custom theme
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={closeSelector}
            className="themed-primary-button h-8 min-w-[130px] justify-center rounded-lg text-sm font-semibold"
          >
            Done
          </button>
        </div>
      </div>
    </StyleSheetManager>
  );
};
